import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Clock, Coins, User, CalendarX2, MapPin } from "lucide-react";
import { get, post } from "@/lib/api";
import { CATEGORY_LABELS, formatDate, BOOKING_STATUS } from "@/lib/data";

export default function MyBookings() {
  const qc = useQueryClient();
  const { data: bookings = [], isLoading } = useQuery({ queryKey: ["bookings"], queryFn: () => get("/bookings") });

  const cancelMut = useMutation({
    mutationFn: (id) => post(`/bookings/${id}/cancel`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["bookings"] });
      qc.invalidateQueries({ queryKey: ["me"] });
      toast.success("Rezervasyon iptal edildi, kredin iade edildi");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "İptal edilemedi"),
  });

  return (
    <div className="mx-auto max-w-4xl px-5 py-10 lg:px-8">
      <h1 className="font-display text-4xl font-semibold tracking-tight sm:text-5xl">Rezervasyonlarım</h1>
      <p className="mt-2 text-[#5C5856]">Yaklaşan ve geçmiş tüm rezervasyonların.</p>

      {isLoading ? (
        <p className="mt-8 text-[#5C5856]">Yükleniyor…</p>
      ) : bookings.length === 0 ? (
        <div data-testid="empty-bookings" className="mt-10 rounded-2xl border border-dashed border-[#E5E0D8] p-14 text-center">
          <CalendarX2 className="mx-auto h-12 w-12 text-[#C9BFB1]" />
          <p className="mt-4 text-[#5C5856]">Henüz rezervasyonun yok.</p>
          <Link to="/kesfet" className="mt-5 inline-block rounded-full bg-[#8C6A5D] px-6 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[#705448]">Seans keşfet</Link>
        </div>
      ) : (
        <div className="mt-8 space-y-4">
          {bookings.map((b) => {
            const st = BOOKING_STATUS[b.status];
            return (
              <div key={b.id} data-testid={`booking-item-${b.id}`} className="flex flex-col gap-4 rounded-2xl border border-[#E5E0D8] bg-white p-5 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium uppercase tracking-wide text-[#8C6A5D]">{CATEGORY_LABELS[b.category]}</span>
                    <span className={`rounded-full border px-2.5 py-0.5 text-xs font-medium ${st.color}`}>{st.label}</span>
                  </div>
                  <h3 className="mt-1 font-display text-xl font-semibold">{b.session_title}</h3>
                  <p className="text-sm text-[#5C5856]">{b.studio_name}</p>
                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-[#5C5856]">
                    <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {formatDate(b.date)} · {b.start_time}</span>
                    {b.instructor && <span className="flex items-center gap-1.5"><User className="h-3.5 w-3.5" /> {b.instructor}</span>}
                    {b.region && <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> {b.region}</span>}
                  </div>
                </div>
                <div className="flex items-center justify-between gap-4 sm:flex-col sm:items-end">
                  <span className="flex items-center gap-1 font-semibold text-[#8C6A5D]"><Coins className="h-4 w-4" /> {b.credit_price}</span>
                  {b.status === "confirmed" && (
                    <button
                      data-testid={`cancel-booking-${b.id}`}
                      onClick={() => cancelMut.mutate(b.id)}
                      disabled={cancelMut.isPending}
                      className="rounded-full border border-[#E5E0D8] px-4 py-1.5 text-sm text-[#5C5856] transition-colors hover:border-rose-300 hover:text-rose-600"
                    >
                      İptal et
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
